/**
 * EnvelopeModal - Visual envelope with letter for address collection
 *
 * Design inspired by a real airmail envelope:
 *   - Letter peeking out the top with message from Caryn
 *   - Envelope body below with address form (underlined inputs)
 *   - Airmail-style border stripes, heart stamp, heart seal
 *
 * Steps:
 *  1. envelope - Envelope with letter + address form
 *  2. did-you-mean - Normalized address suggestion
 *  3. confirm - Review address
 *  4. success - Done
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { ConfirmationStep } from './ConfirmationStep'
import {
  submitAddress,
  validateAddress,
  autocompleteAddress,
  type AddressData,
  type ValidationResult,
  type AddressSuggestion
} from './mailingService'
import { useAuth } from '@/features/auth'
import { CREATOR_INITIALS } from '@/config/brandConfig'
import { CheckCircle, X } from 'lucide-react'

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
]

type Step = 'envelope' | 'did-you-mean' | 'confirm' | 'success'

interface EnvelopeModalProps {
  isOpen: boolean
  onClose: () => void
  onAddressSaved?: () => void
}

// Airmail border style (red/blue diagonal stripes)
const airmailBorder = {
  background: `repeating-linear-gradient(
    -45deg,
    #e8607080 0px, #e8607080 4px,
    transparent 4px, transparent 8px,
    #6b8dd680 8px, #6b8dd680 12px,
    transparent 12px, transparent 16px
  )`
}

export function EnvelopeModal({ isOpen, onClose, onAddressSaved }: EnvelopeModalProps) {
  const { user } = useAuth()
  const [step, setStep] = useState<Step>('envelope')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<AddressData | null>(null)
  const [validation, setValidation] = useState<ValidationResult | null>(null)

  // Form fields
  const defaultName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || ''
  const [fullName, setFullName] = useState('')
  const [addressLine1, setAddressLine1] = useState('')
  const [addressLine2, setAddressLine2] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Autocomplete
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [acLoading, setAcLoading] = useState(false)
  const acTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  const resetState = useCallback(() => {
    setStep('envelope')
    setFormData(null)
    setValidation(null)
    setError(null)
    setIsSubmitting(false)
    setFullName('')
    setAddressLine1('')
    setAddressLine2('')
    setCity('')
    setState('')
    setZipCode('')
    setFieldErrors({})
    setSuggestions([])
    setShowSuggestions(false)
  }, [])

  // Initialize name from user profile
  useEffect(() => {
    if (isOpen && !fullName && defaultName) {
      setFullName(defaultName)
    }
  }, [isOpen, defaultName])

  // Restore form data if going back to envelope step
  useEffect(() => {
    if (step === 'envelope' && formData) {
      setFullName(formData.full_name)
      setAddressLine1(formData.address_line1)
      setAddressLine2(formData.address_line2 || '')
      setCity(formData.city)
      setState(formData.state)
      setZipCode(formData.zip_code)
    }
  }, [step])

  function handleClose() {
    resetState()
    onClose()
  }

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => { document.body.style.overflow = 'unset' }
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) handleClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Address autocomplete handler
  function handleAddressInput(value: string) {
    setAddressLine1(value)

    if (acTimerRef.current) clearTimeout(acTimerRef.current)

    if (value.trim().length < 3) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    acTimerRef.current = setTimeout(async () => {
      setAcLoading(true)
      try {
        const results = await autocompleteAddress(value)
        setSuggestions(results)
        setShowSuggestions(results.length > 0)
      } catch {
        setSuggestions([])
      } finally {
        setAcLoading(false)
      }
    }, 300)
  }

  function selectSuggestion(s: AddressSuggestion) {
    setAddressLine1(s.street_line)
    setAddressLine2(s.secondary || '')
    setCity(s.city)
    setState(s.state)
    setZipCode(s.zipcode)
    setSuggestions([])
    setShowSuggestions(false)
  }

  // Form validation
  function validateForm(): boolean {
    const errors: Record<string, string> = {}
    if (!fullName.trim()) errors.full_name = 'Required'
    if (!addressLine1.trim()) errors.address_line1 = 'Required'
    if (!city.trim()) errors.city = 'Required'
    if (!state) errors.state = 'Required'
    if (!zipCode.trim()) {
      errors.zip_code = 'Required'
    } else if (!/^\d{5}(-\d{4})?$/.test(zipCode.trim())) {
      errors.zip_code = 'Invalid ZIP'
    }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  async function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validateForm()) return

    const data: AddressData = {
      full_name: fullName.trim(),
      address_line1: addressLine1.trim(),
      address_line2: addressLine2.trim() || undefined,
      city: city.trim(),
      state: state.toUpperCase(),
      zip_code: zipCode.trim()
    }

    setFormData(data)
    setError(null)
    setIsSubmitting(true)

    try {
      const result = await validateAddress({
        address_line1: data.address_line1,
        address_line2: data.address_line2,
        city: data.city,
        state: data.state,
        zip_code: data.zip_code
      })

      setValidation(result)

      if (!result.valid) {
        setError(result.reason || 'This address could not be verified. Please check and try again.')
        setIsSubmitting(false)
        return
      }

      const wasNormalized = result.normalized && (
        result.normalized.address_line1 !== data.address_line1 ||
        result.normalized.city !== data.city ||
        result.normalized.state !== data.state ||
        result.normalized.zip_code !== data.zip_code
      )

      setStep(wasNormalized ? 'did-you-mean' : 'confirm')
    } catch {
      setError('Failed to validate address. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleAcceptNormalized() {
    setStep('confirm')
  }

  function handleKeepOriginal() {
    setValidation((prev) => prev ? { ...prev, normalized: null } : null)
    setStep('confirm')
  }

  async function handleConfirm() {
    if (!formData) return
    setIsSubmitting(true)
    setError(null)

    try {
      const result = await submitAddress(formData)

      if (result.validation_error) {
        setError(result.reason || 'Address validation failed.')
        setStep('envelope')
        setIsSubmitting(false)
        return
      }

      if (result.success) {
        setStep('success')
        onAddressSaved?.()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save address. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  const inputClass = 'w-full py-1.5 px-1 bg-transparent border-b border-pink-300 text-sm text-gray-800 placeholder-pink-300/70 focus:outline-none focus:border-pink-500 transition-colors'
  const labelClass = 'text-xs font-medium text-pink-600 flex items-center gap-1'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-sm animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        {step !== 'success' && (
          <div className="flex justify-end mb-2">
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-500 hover:text-gray-700 transition-colors shadow-sm"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* MAIN VIEW: Envelope with letter */}
        {step === 'envelope' && (
          <div className="relative">

            {/* === LETTER peeking out of envelope === */}
            <div className="relative mx-5 -mb-3 z-10">
              {/* Airmail border wrapper */}
              <div className="rounded-t-md p-[5px]" style={airmailBorder}>
                {/* White letter paper */}
                <div className="bg-white rounded-t-sm border border-dashed border-pink-200 px-4 pt-4 pb-6 relative">

                  {/* Stamp - top right */}
                  <div className="absolute top-2 right-2 w-8 h-9 border-2 border-dashed border-pink-300 rounded-sm flex items-center justify-center bg-pink-50">
                    <span className="text-pink-400 text-sm">&#9829;</span>
                  </div>

                  {/* Letter text */}
                  <div>
                    <p className="text-gray-800 text-sm font-semibold leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
                      Want to be on my mailing list? 💗
                    </p>
                  </div>
                  <div className="mt-3">
                    <p className="text-gray-600 text-xs leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
                      I want to send you letters and gifts and will be mailing out something very special soon. If you want to be on the list make sure you sign up below. I can&#39;t wait to send you stuff! 😉
                    </p>
                  </div>

                  {/* Signature */}
                  <div className="mt-3 border-t border-pink-200 pt-2 text-right">
                    <p className="text-pink-500 text-sm" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
                      - {CREATOR_INITIALS.toUpperCase()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* === ENVELOPE body === */}
            <div className="relative">
              {/* Airmail border wrapper */}
              <div className="rounded-b-md p-[5px]" style={airmailBorder}>
                {/* Pink envelope body */}
                <div className="bg-pink-50 rounded-b-sm pt-4 px-4 pb-4">
                  {/* Error banner */}
                  {error && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleFormSubmit} className="space-y-3">
                    {/* Full Name */}
                    <div>
                      <label className={labelClass}>
                        <span className="text-pink-400">&#9829;</span> Full name:
                      </label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Your full name"
                        className={inputClass}
                        disabled={isSubmitting}
                        autoComplete="name"
                      />
                      {fieldErrors.full_name && <p className="text-xs text-red-500 mt-0.5">{fieldErrors.full_name}</p>}
                    </div>

                    {/* Mailing Address */}
                    <div className="space-y-2">
                      <label className={labelClass}>
                        <span className="text-pink-400 text-xs">&#127968;</span> Mailing address:
                      </label>

                      {/* Street with autocomplete */}
                      <div className="relative" ref={suggestionsRef}>
                        <input
                          type="text"
                          value={addressLine1}
                          onChange={(e) => handleAddressInput(e.target.value)}
                          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true) }}
                          placeholder="Start typing your street address..."
                          className={inputClass}
                          disabled={isSubmitting}
                          autoComplete="off"
                        />
                        {acLoading && (
                          <div className="absolute right-1 top-1.5">
                            <div className="w-3 h-3 border border-pink-300 border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}

                        {/* Autocomplete dropdown */}
                        {showSuggestions && suggestions.length > 0 && (
                          <div className="absolute z-30 left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-pink-200 max-h-40 overflow-y-auto">
                            {suggestions.map((s, i) => (
                              <button
                                key={i}
                                type="button"
                                className="w-full text-left px-3 py-2 text-xs hover:bg-pink-50 transition-colors border-b border-pink-50 last:border-0"
                                onClick={() => selectSuggestion(s)}
                              >
                                <span className="font-medium text-gray-800">{s.street_line}</span>
                                {s.secondary && <span className="text-gray-500"> {s.secondary}</span>}
                                <br />
                                <span className="text-gray-500">{s.city}, {s.state} {s.zipcode}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        {fieldErrors.address_line1 && <p className="text-xs text-red-500 mt-0.5">{fieldErrors.address_line1}</p>}
                      </div>

                      {/* Apt/Suite */}
                      <input
                        type="text"
                        value={addressLine2}
                        onChange={(e) => setAddressLine2(e.target.value)}
                        placeholder="Apt, Suite, Unit (optional)"
                        className={inputClass}
                        disabled={isSubmitting}
                        autoComplete="address-line2"
                      />

                      {/* City, State, ZIP in one row */}
                      <div className="flex gap-2">
                        <div className="flex-1 min-w-0">
                          <input
                            type="text"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            placeholder="City"
                            className={inputClass}
                            disabled={isSubmitting}
                            autoComplete="address-level2"
                          />
                          {fieldErrors.city && <p className="text-xs text-red-500 mt-0.5">{fieldErrors.city}</p>}
                        </div>
                        <div className="w-16">
                          <select
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                            className={`${inputClass} appearance-none`}
                            disabled={isSubmitting}
                          >
                            <option value="">ST</option>
                            {US_STATES.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                          {fieldErrors.state && <p className="text-xs text-red-500 mt-0.5">{fieldErrors.state}</p>}
                        </div>
                        <div className="w-20">
                          <input
                            type="text"
                            value={zipCode}
                            onChange={(e) => setZipCode(e.target.value)}
                            placeholder="ZIP"
                            maxLength={10}
                            className={inputClass}
                            disabled={isSubmitting}
                            autoComplete="postal-code"
                          />
                          {fieldErrors.zip_code && <p className="text-xs text-red-500 mt-0.5">{fieldErrors.zip_code}</p>}
                        </div>
                      </div>
                    </div>

                    {/* Heart seal */}
                    <div className="text-center pt-1">
                      <span className="inline-block text-pink-400 text-xl">&#9829;</span>
                    </div>

                    {/* Submit button */}
                    <button
                      type="submit"
                      className="w-full py-2 text-sm font-medium text-white bg-pink-500 rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Validating...' : 'Send'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* DID YOU MEAN step */}
        {step === 'did-you-mean' && validation?.normalized && formData && (
          <div className="bg-white rounded-lg shadow-2xl p-6 space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">Did you mean?</h3>
              <p className="text-sm text-gray-500 mt-1">We found a standardized version</p>
            </div>

            <div
              className="bg-pink-50 border-2 border-pink-300 rounded-lg p-4 cursor-pointer hover:bg-pink-100 transition-colors"
              onClick={handleAcceptNormalized}
            >
              <p className="text-xs font-medium text-pink-600 mb-1">Suggested (USPS verified)</p>
              <p className="text-sm text-gray-900 font-medium">{formData.full_name}</p>
              <p className="text-sm text-gray-700">{validation.normalized.address_line1}</p>
              {formData.address_line2 && <p className="text-sm text-gray-700">{formData.address_line2}</p>}
              <p className="text-sm text-gray-700">{validation.normalized.city}, {validation.normalized.state} {validation.normalized.zip_code}</p>
            </div>

            <div
              className="bg-gray-50 border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={handleKeepOriginal}
            >
              <p className="text-xs font-medium text-gray-500 mb-1">Original (as entered)</p>
              <p className="text-sm text-gray-900 font-medium">{formData.full_name}</p>
              <p className="text-sm text-gray-700">{formData.address_line1}</p>
              {formData.address_line2 && <p className="text-sm text-gray-700">{formData.address_line2}</p>}
              <p className="text-sm text-gray-700">{formData.city}, {formData.state} {formData.zip_code}</p>
            </div>

            <button
              onClick={() => setStep('envelope')}
              className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Edit address
            </button>
          </div>
        )}

        {/* CONFIRM step */}
        {step === 'confirm' && formData && (
          <div className="bg-white rounded-lg shadow-2xl p-6">
            <ConfirmationStep
              fullName={formData.full_name}
              addressLine1={formData.address_line1}
              addressLine2={formData.address_line2}
              city={formData.city}
              state={formData.state}
              zipCode={formData.zip_code}
              normalized={validation?.normalized}
              validationNote={validation?.reason}
              onConfirm={handleConfirm}
              onGoBack={() => setStep('envelope')}
              isSubmitting={isSubmitting}
            />
          </div>
        )}

        {/* SUCCESS step */}
        {step === 'success' && (
          <div className="bg-white rounded-lg shadow-2xl p-6 text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Address Saved!</h3>
              <p className="text-sm text-gray-500 mt-1">
                Keep an eye on your mailbox for something special!
              </p>
            </div>
            <button
              onClick={handleClose}
              className="w-full py-2 text-sm font-medium text-white bg-pink-500 rounded-lg hover:bg-pink-600 transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
